import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ChevronDown, ChevronUp } from "lucide-react";
import dinnerImage from "../assets/images/people.webp";
import Image from "./ux/Image";
import { useSearchCities } from "../lib/api";
import CitySelect from "./quiz/CitySelect";

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getNextThursdayAt7PM(): Date {
  // Create a date in NYC timezone
  const now = new Date();
  const nyc = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  });

  // Parse NYC time
  const parts = nyc.formatToParts(now);
  const nycYear = parseInt(parts.find((p) => p.type === "year")?.value || "0");
  const nycMonth =
    parseInt(parts.find((p) => p.type === "month")?.value || "0") - 1;
  const nycDay = parseInt(parts.find((p) => p.type === "day")?.value || "0");
  const nycHour = parseInt(parts.find((p) => p.type === "hour")?.value || "0");
  const nycMinute = parseInt(
    parts.find((p) => p.type === "minute")?.value || "0"
  );

  // Create a date object representing current NYC time
  const nycNow = new Date(nycYear, nycMonth, nycDay, nycHour, nycMinute);

  // Calculate next Thursday
  const daysUntilThursday = (4 - nycNow.getDay() + 7) % 7;
  const nextThursday = new Date(
    nycYear,
    nycMonth,
    nycDay + daysUntilThursday,
    19,
    0,
    0,
    0
  );

  // If it's Thursday but past 7PM, add another week
  if (nycNow.getDay() === 4 && nycNow.getHours() >= 19) {
    nextThursday.setDate(nextThursday.getDate() + 7);
  }

  // Convert back to a proper UTC date
  const nycString = nextThursday.toLocaleString("en-US", {
    timeZone: "America/New_York",
  });
  const utcDate = new Date(nycString + " GMT-0500");

  return utcDate;
}

function calculateTimeRemaining(targetDate: Date): TimeRemaining {
  const now = new Date().getTime();
  const target = targetDate.getTime();
  const difference = target - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
}

const QUIZ_ANSWERS_KEY = "quizAnswers";

function getStoredLocation(): string {
  try {
    const raw = localStorage.getItem(QUIZ_ANSWERS_KEY);
    if (!raw) return "";
    const answers = JSON.parse(raw) as Record<string, unknown>;
    return typeof answers.location === "string" ? answers.location : "";
  } catch {
    return "";
  }
}

function persistLocationToQuiz(cityId: string): void {
  try {
    const raw = localStorage.getItem(QUIZ_ANSWERS_KEY);
    const existing = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    localStorage.setItem(
      QUIZ_ANSWERS_KEY,
      JSON.stringify({ ...existing, location: cityId })
    );
  } catch {
    // ignore
  }
}

export default function NextDinner({ avatarImage, avatarClassName }: { avatarImage?: string; avatarClassName?: string } = {}) {
  const [selectedCity, setSelectedCity] = useState(getStoredLocation);
  const [showCitySelector, setShowCitySelector] = useState(false);
  const nextDinnerRef = useRef<Date>(getNextThursdayAt7PM());
  const daysRef = useRef<HTMLSpanElement>(null);
  const hoursRef = useRef<HTMLSpanElement>(null);
  const minutesRef = useRef<HTMLSpanElement>(null);
  const secondsRef = useRef<HTMLSpanElement>(null);

  const { data: citiesData } = useSearchCities("", selectedCity);
  const selectedCityData = citiesData?.data?.find((c: { id: string }) => c.id === selectedCity);
  const cityQuestion = { id: "city", type: "city" as const, question: "" };

  const handleCitySelect = (cityId: string) => {
    setSelectedCity(cityId);
    setShowCitySelector(false);
    persistLocationToQuiz(cityId);
  };

  useEffect(() => {
    // Refs instead of state to avoid React re-renders
    const updateDisplay = (remaining: TimeRemaining) => {
      if (daysRef.current) daysRef.current.textContent = remaining.days.toString().padStart(2, "0");
      if (hoursRef.current) hoursRef.current.textContent = remaining.hours.toString().padStart(2, "0");
      if (minutesRef.current) minutesRef.current.textContent = remaining.minutes.toString().padStart(2, "0");
      if (secondsRef.current) secondsRef.current.textContent = remaining.seconds.toString().padStart(2, "0");
    };

    // Initial display
    updateDisplay(calculateTimeRemaining(nextDinnerRef.current));

    // Update countdown every second via DOM manipulation (no React re-renders)
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining(nextDinnerRef.current);
      updateDisplay(remaining);

      // If countdown reached zero, calculate next dinner
      if (
        remaining.days === 0 &&
        remaining.hours === 0 &&
        remaining.minutes === 0 &&
        remaining.seconds === 0
      ) {
        nextDinnerRef.current = getNextThursdayAt7PM();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="w-full max-w-2xl mx-auto my-auto rounded-2xl overflow-hidden "
    >
      <div className="flex flex-col p-0 overflow-hidden items-stretch">
        <div className="flex-1 p-4 md:p-6 flex flex-col justify-between">
          <div className="text-center">
              <h2 className="inline-block px-8 text-xl font-semibold text-white">
                Next Dinner in...
              </h2>
            <Image
              src={avatarImage ?? dinnerImage}
              fit="contain"
              alt="Next Dinner"
              className={avatarClassName ?? "h-16 w-full mx-auto my-4"}
              />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "d", ref: daysRef },
              { label: "h", ref: hoursRef },
              { label: "m", ref: minutesRef },
              { label: "s", ref: secondsRef },
            ].map((item) => (
              <div
                key={item.label}
                className="p-2 rounded-full bg-white dark:bg-neutral-900"
              >
                <div className="text-xl md:text-3xl font-bold text-type-950">
                  <span ref={item.ref}>00</span>
                  <span className="text-sm text-dark-muted tracking-wide mt-2 inline">
                    {item.label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col w-full mx-auto ">
            <button
              type="button"
              onClick={() => setShowCitySelector((open) => !open)}
              className="flex items-center justify-between rounded-xl border border-divider bg-white dark:bg-neutral-900 px-4 py-3 text-left transition-colors hover:border-primary-500/50 group"
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                    selectedCity ? "bg-primary-500/20" : "bg-dark-border dark:bg-neutral-800"
                  }`}
                >
                  <MapPin
                    className={`w-5 h-5 ${
                      selectedCity ? "text-primary-500" : "text-dark-muted"
                    }`}
                  />
                </div>
                <div>
                  <div className="text-xs text-dark-muted mb-0.5">City</div>
                  <div className="text-sm font-semibold text-type-950 dark:text-neutral-100 flex items-center gap-2 flex-wrap">
                    {selectedCityData?.name ?? "Choose your city"}
                    {selectedCityData && selectedCityData.available === false && (
                      <span className="text-xs font-normal text-primary-600 dark:text-primary-400">
                        Coming soon
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {showCitySelector ? (
                <ChevronUp className="w-5 h-5 text-dark-muted group-hover:text-primary-500 transition-colors shrink-0" />
              ) : (
                <ChevronDown className="w-5 h-5 text-dark-muted group-hover:text-primary-500 transition-colors shrink-0" />
              )}
            </button>

            <AnimatePresence>
              {showCitySelector && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden card rounded-xl"
                >
                  <div className="pt-4">
                    <CitySelect
                      question={cityQuestion}
                      value={selectedCity}
                      onChange={handleCitySelect}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          <a
            href={selectedCity ? `https://app.outclose.com/quiz?city=${selectedCity}` : "https://app.outclose.com/quiz"}
            className="btn btn-primary px-8 py-4 mt-4 mx-auto"
          >
            Sign up now
          </a>
          </div>

        </div>
      </div>
    </div>
  );
}
